import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useDeleteData from "../../../../hooks/useDeleteData";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";
import Edit from "./Edit";

const List = () => {
  const location = useLocation();
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentTaskType, setCurrentTaskType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/task-type`);
      console.log("Task Types data:", res.data.data);
      setTaskTypes(res.data.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const deleteData = useDeleteData(fetchData);

  const handleEdit = (taskType) => {
    setCurrentTaskType(taskType);
    setEditVisible(true);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Helper function to safely display user information
  const displayUserInfo = (userObject, userId) => {
    if (!userObject && !userId) return "N/A";

    // If we have a user object with the name property
    if (userObject && typeof userObject === "object") {
      return (
        <div>
          {userObject.name || `${userObject.first_name || ""} ${userObject.last_name || ""}`}
          {!userObject.name && !userObject.first_name && !userObject.last_name && `User ID: ${userObject.id || "Unknown"}`}
        </div>
      );
    }

    // If only userId is available
    return <div>User ID: {userId}</div>;
  };

  // Filter task types based on search term
  const filteredTaskTypes = taskTypes.filter((type) => type.task_type.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Task Types</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search task types..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
            </div>
            <div>
              <Button label="Create Task Type" icon="pi pi-plus" className="p-button-success" onClick={() => setVisible(true)} />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading task types...</p>
              </div>
            ) : filteredTaskTypes.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No task types found. Create your first task type!</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mg-b-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Icon</th>
                      <th>Color</th>
                      <th>Preview</th>
                      <th>Created By</th>
                      <th>Updated By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTaskTypes.map((taskType) => (
                      <tr key={taskType.id}>
                        <td>{taskType.task_type}</td>
                        <td>
                          <code>{taskType.icon || "No icon"}</code>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "25px",
                                height: "25px",
                                backgroundColor: taskType.color || "#6c757d",
                                borderRadius: "4px",
                                marginRight: "10px",
                                border: "1px solid #ddd",
                              }}
                            ></div>
                            <span>{taskType.color || "No color"}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: taskType.color || "#6c757d",
                              color: "#fff",
                              padding: "8px 12px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            {taskType.icon && <i className={taskType.icon}></i>}
                            {taskType.task_type}
                          </span>
                        </td>
                        <td>
                          <div>
                            {displayUserInfo(taskType.created_by, null)}
                            <small className="text-muted d-block">{formatDate(taskType.created_at)}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            {displayUserInfo(taskType.updated_by, null)}
                            <small className="text-muted d-block">{formatDate(taskType.updated_at)}</small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex">
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-warning p-button-sm mr-2"
                              onClick={() => handleEdit(taskType)}
                              tooltip="Edit"
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-danger p-button-sm"
                              onClick={() => deleteData(taskType.id, "task-type", "task-type")}
                              tooltip="Delete"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog header="Create Task Type" visible={visible} style={{ width: "50vw" }} onHide={() => setVisible(false)} dismissableMask>
        <Create setVisible={setVisible} fetchData={fetchData} />
      </Dialog>

      {/* Edit Dialog */}
      <Dialog header="Edit Task Type" visible={editVisible} style={{ width: "50vw" }} onHide={() => setEditVisible(false)} dismissableMask>
        <Edit setVisible={setEditVisible} fetchData={fetchData} rowData={currentTaskType} />
      </Dialog>
    </div>
  );
};

export default List;
